import {
  PortalCreateEconsultRequest,
  PortalReplyEconsultRequest,
  ResponseTemplateSearchRequest,
} from "@shared/dtos/requests/econsultRequests.dto"
import { PageRequest } from "@shared/dtos/requests/pageRequest.dto"
import {
  EconsultCreationResponse,
  EconsultDetailPortalResponse,
  EconsultPageResponse,
  IcpcResponse,
  ResponseTemplateSearchResponse,
  UsefulLinkResponse,
} from "@shared/dtos/responses/econsultResponses.dto"
import { SendToHisEconsultRequest } from "@shared/zod/schemas/requests/econsult"

import { axiosAuth } from "./generic/AxiosInterceptor/AxiosInterceptor"
import { makeFileInstance } from "./generic/FileInstance"

export const findAll = (query?: PageRequest): Promise<EconsultPageResponse> => {
  return axiosAuth({
    url: "/econsults",
    method: "get",
    params: query,
  }).then((resp) => {
    return resp.data
  })
}

export const get = (id: string): Promise<EconsultDetailPortalResponse> => {
  return axiosAuth({
    url: `/econsults/${id}`,
    method: "get",
  }).then((resp) => resp.data)
}

export const responseByCaregiver = (id: string, req: PortalReplyEconsultRequest): Promise<void> => {
  return axiosAuth({
    url: `/econsults/${id}`,
    method: "post",
    data: req,
  }).then(() => undefined)
}

export const downloadAttachment = (econsultId: string, messageId: string, attachmentId: string) => {
  return axiosAuth({
    url: `/econsults/${econsultId}/${messageId}/attachment/${attachmentId}`,
    method: "get",
    responseType: "blob",
  }).then(makeFileInstance)
}

export const getUsefulLinks = async (query: string): Promise<UsefulLinkResponse[]> => {
  const result = await axiosAuth({
    url: `/econsults/usefullinks`,
    method: "get",
    params: { query },
  })
  return result.data
}

export const responseTemplateSearch = async (
  patientId: string,
  req: ResponseTemplateSearchRequest
): Promise<ResponseTemplateSearchResponse[]> => {
  const result = await axiosAuth({
    url: `/econsults/responsetemplates/${patientId}`,
    method: "get",
    params: req,
  })
  return result.data
}

export const responseTemplateFavourite = async (id: string): Promise<void> => {
  await axiosAuth({
    url: `/econsults/responsetemplates/favourite/${id}`,
    method: "put",
  })
  return undefined
}

export const responseTemplateUnfavourite = async (id: string): Promise<void> => {
  await axiosAuth({
    url: `/econsults/responsetemplates/favourite/${id}`,
    method: "delete",
  })
  return undefined
}

export const getIcpcs = async (query: string): Promise<IcpcResponse[]> => {
  const result = await axiosAuth({
    url: `/econsults/icpc`,
    method: "get",
    params: { query },
  })
  return result.data
}

export const portalCreate = (req: PortalCreateEconsultRequest): Promise<EconsultCreationResponse> => {
  return axiosAuth({
    url: "/econsults",
    method: "post",
    data: req,
  }).then((result) => result.data)
}

export const close = (econsultId: string): Promise<void> => {
  return axiosAuth({
    url: `/econsults/${econsultId}/close`,
    method: "post",
    data: {},
  }).then(() => undefined)
}

export const reopen = (econsultId: string): Promise<void> => {
  return axiosAuth({
    url: `/econsults/${econsultId}/close`,
    method: "delete",
    data: {},
  }).then(() => undefined)
}

export const sendToHis = (econsultId: string, data: SendToHisEconsultRequest): Promise<void> => {
  return axiosAuth({
    url: `/econsults/${econsultId}/sendtohis`,
    method: "post",
    data,
  }).then(() => undefined)
}
